import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import SLACountdown from "../shared/SLACountdown";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";
import { apiErrorCode, apiErrorMessage } from "../../../utils/apiError";

const TYPE_TABS = [
  { value: "",               label: "All types" },
  { value: "KYC",            label: "KYC" },
  // Person-first P4: the company's review (KYB) is its own queue type; the
  // API's queue accepts type=KYB (UI-06).
  { value: "KYB",            label: "Company (KYB)" },
  { value: "CLASSIFICATION", label: "Classification" },
  { value: "SUITABILITY",    label: "Suitability" },
];

const SCOPE_TABS = [
  { value: "mine", label: "Mine" },
  { value: "all",  label: "All" },
];

const SLA_TABS = [
  { value: "",         label: "All SLA" },
  { value: "WITHIN",   label: "Within" },
  { value: "AT_RISK",  label: "At risk" },
  { value: "BREACHED", label: "Breached" },
];

// What the claim endpoint refuses with, in words a reviewer can act on. The
// raw tokens are the service's vocabulary, not theirs.
const CLAIM_ERROR_COPY = {
  already_claimed: "Already claimed by another reviewer",
  credential_expired:
    "Your reviewer credential has expired. Renew it on the Credentials page before claiming.",
  dr_credential_expired_or_missing:
    "Your reviewer credential is missing or expired. Renew it on the Credentials page before claiming.",
  not_open: "This item is no longer open. Refreshing the queue.",
};

const STATUS_CLASS = {
  PENDING:   "s-rev",
  COMPLETED: "s-active",
  BREACHED:  "s-fail",
  CANCELLED: "s-mute",
};

function investorName(sla) {
  const inv = sla?.Investor;
  if (!inv) return `User #${sla.investor_user_id}`;
  const first = inv.firstname || "";
  const last = inv.lastname || "";
  const full = `${first} ${last}`.trim();
  return full || inv.email || `User #${inv.id}`;
}

// The queue flattens the holder to `assigned_reviewer {id,name}` and leaves
// the raw AssignedReviewer / AssignedDr associations alongside it. Prefer the
// flattened one (it is what the endpoint documents) and keep the associations
// as the fallback so a row from the older shape still names somebody.
function reviewerName(sla) {
  const flat = sla?.assigned_reviewer;
  if (flat) return flat.name || `Reviewer #${flat.id}`;
  const d = sla?.AssignedReviewer || sla?.AssignedDr;
  if (d) {
    const full = `${d.firstname || ""} ${d.lastname || ""}`.trim();
    return full || d.email || `Reviewer #${d.id}`;
  }
  if (sla?.assigned_reviewer_user_id) return `Reviewer #${sla.assigned_reviewer_user_id}`;
  return "Unassigned";
}

function isUnassigned(sla) {
  return (
    !sla?.assigned_reviewer &&
    !sla?.assigned_reviewer_user_id &&
    !sla?.AssignedReviewer &&
    !sla?.AssignedDr
  );
}

/**
 * The deal this item belongs to. The row carries `campaign {id,name,slug}`, so
 * name the campaign rather than printing its id at a reviewer: "Campaign 903"
 * is not something anybody recognises.
 */
function productRef(sla) {
  const parts = [];
  const campaign = sla?.campaign;
  if (campaign) parts.push(campaign.name || `Campaign ${campaign.id}`);
  else if (sla?.campaign_id) parts.push(`Campaign ${sla.campaign_id}`);
  if (sla?.investment_id) parts.push(`Investment ${sla.investment_id}`);
  return parts.length ? parts.join(" / ") : "-";
}

function DRApprovalQueue() {
  const navigate = useNavigate();
  // A session that decides (DR, CCO, UDP) claims and works this queue. A
  // Platform Manager holding no compliance role reads it and hands items on
  // from the detail screen, so it gets neither the Claim action nor the
  // reviewer-personal credential banner.
  const { canDecide } = useRole();
  const [typeFilter, setTypeFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState("");
  // "all" keeps the pre-scope behaviour of this queue as the landing view; a
  // reviewer narrows to their own claimed items deliberately.
  const [scope, setScope] = useState("all");
  const [claimingId, setClaimingId] = useState(null);
  const [flash, setFlash] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter changes fire overlapping requests and none of them cancel: without
  // a token a slow first response lands after a fast second one and the table
  // contradicts the filters on screen.
  const requestRef = useRef(0);

  const fetchQueue = useCallback(() => {
    const token = ++requestRef.current;
    const current = () => requestRef.current === token;
    setLoading(true);
    const params = { page, limit };
    if (typeFilter) params.type = typeFilter;
    if (slaFilter) params.sla = slaFilter;
    // Only a session that can claim has a "mine" to scope to.
    if (canDecide) params.scope = scope;

    return drApi
      .approvalsQueue(params)
      .then((res) => {
        if (!current()) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!current()) return;
        setError(apiErrorMessage(err, "Fetch failed"));
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
  }, [typeFilter, slaFilter, scope, canDecide, page, limit]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Claiming is a race by construction: two reviewers can open the same queue.
  // A 409 is the normal losing outcome, not an error, so it refetches and says
  // what happened rather than showing a red failure.
  async function claim(sla) {
    setClaimingId(sla.id);
    setFlash(null);
    try {
      await drApi.claim(sla.id, {});
      setFlash({ tone: "info", text: "Claimed. This item is now assigned to you." });
      await fetchQueue();
    } catch (err) {
      const copy = CLAIM_ERROR_COPY[apiErrorCode(err)];
      if (copy) {
        // Every one of these is a state the reviewer can do something about,
        // so none of them is a red failure: say what happened and resync.
        setFlash({ tone: "warn", text: copy });
        await fetchQueue();
        return;
      }
      if (err?.response?.status === 409) {
        setFlash({ tone: "warn", text: CLAIM_ERROR_COPY.already_claimed });
        await fetchQueue();
        return;
      }
      setFlash({ tone: "danger", text: apiErrorMessage(err, "Claim failed") });
    } finally {
      setClaimingId(null);
    }
  }

  const subtitle = (
    <>
      <span className="mono">{total}</span>
      <span>item{total === 1 ? "" : "s"}</span>
      <span>·</span>
      <span className="mono">page {page} of {Math.max(totalPages, 1)}</span>
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Investor approvals" subtitle={subtitle} />

      {canDecide && <CredentialExpiryAlert />}

      <div className="toolbar">
        <span className="field__label" style={{ marginRight: 4 }}>Submission type</span>
        <div className="toolbar__pills">
          {TYPE_TABS.map((t) => (
            <button
              key={t.value || "all"}
              type="button"
              className={`toolbar__pill${typeFilter === t.value ? " toolbar__pill--on" : ""}`}
              onClick={() => { setTypeFilter(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {canDecide && (
        <div className="toolbar">
          <span className="field__label" style={{ marginRight: 4 }}>Scope</span>
          <div className="toolbar__pills">
            {SCOPE_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`toolbar__pill${scope === t.value ? " toolbar__pill--on" : ""}`}
                onClick={() => { setScope(t.value); setPage(1); }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="toolbar">
        <span className="field__label" style={{ marginRight: 4 }}>SLA band</span>
        <div className="toolbar__pills">
          {SLA_TABS.map((t) => (
            <button
              key={t.value || "all"}
              type="button"
              className={`toolbar__pill${slaFilter === t.value ? " toolbar__pill--on" : ""}`}
              onClick={() => { setSlaFilter(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

      {rows.length === 0 ? (
        <Empty>
          No items in this queue. Filters: type="{typeFilter || "any"}", SLA="{slaFilter || "any"}".
        </Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Investor</th>
                  <th>Type</th>
                  <th>Campaign / Investment</th>
                  <th>Submitted</th>
                  <th>SLA</th>
                  <th>Status</th>
                  <th>Reviewer</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((sla) => {
                  const goDetail = () => navigate(`/dr/approvals/${sla.id}`);
                  const statusUpper = String(sla.status || "").toUpperCase();
                  return (
                    <tr
                      key={sla.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`Review ${sla.submission_type || "SLA"} for ${investorName(sla)}`}
                      onClick={goDetail}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goDetail();
                        }
                      }}
                    >
                      <td>
                        <div>{investorName(sla)}</div>
                        <div className="tbl-id">{sla?.Investor?.email || "—"}</div>
                      </td>
                      <td className="muted">{sla.submission_type}</td>
                      <td className="muted">{productRef(sla)}</td>
                      <td className="muted">{new Date(sla.submitted_at).toLocaleDateString()}</td>
                      <td>
                        <SLACountdown deadline={sla.deadline_at} />
                      </td>
                      <td>
                        <span className={`tbl-status ${STATUS_CLASS[statusUpper] || "s-mute"}`}>
                          {sla.status || "—"}
                        </span>
                      </td>
                      <td className="muted">{reviewerName(sla)}</td>
                      <td className="r">
                        {canDecide && isUnassigned(sla) && (
                          <Btn
                            variant="primary"
                            size="sm"
                            disabled={claimingId === sla.id}
                            onClick={(e) => { e.stopPropagation(); claim(sla); }}
                          >
                            {claimingId === sla.id ? "Claiming..." : "Claim"}
                          </Btn>
                        )}
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); goDetail(); }}
                        >
                          Review <ChevronRight />
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default DRApprovalQueue;
